/**
 * Admin: Bulk classify recipes that were imported without a `type`.
 *
 * Feb 2026 workflow — CSV imports may leave Type empty. Those recipes land
 * here where the admin picks a type per recipe (or in bulk via "sæt alle
 * synlige til <type>") and one save-button writes them all. First-time
 * type assignment auto-fills `target_brix` server-side using the
 * TYPE_DEFAULT_BRIX map (same behaviour as EditRecipePage.js).
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { API } from '../App';

const TYPE_OPTIONS = [
  { key: 'klassisk',  label: 'Klassisk',   brix: 13 },
  { key: 'juice',     label: 'Juice',      brix: 10 },
  { key: 'sodavand',  label: 'Sodavand',   brix: 10 },
  { key: 'smoothie',  label: 'Smoothie',   brix: 15 },
  { key: 'maelk',     label: 'Mælk',       brix: 15 },
  { key: 'kaffe',     label: 'Kaffe',      brix: 12 },
  { key: 'cocktail',  label: 'Cocktail',   brix: 16 },
  { key: 'sukkerfri', label: 'Sukkerfri',  brix: 2  },
  { key: 'sport',     label: 'Sport',      brix: 8  },
];

const AdminClassifyRecipesPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [selections, setSelections] = useState({}); // { recipe_id: type_key }
  const [bulkType, setBulkType] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/recipes/needing-type`, {
        params: { include_unpublished: true },
      });
      setRows(r.data.recipes || []);
      setSelections({}); // reset selections on reload
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunne ikke hente listen');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      (r.name || '').toLowerCase().includes(q) ||
      (r.description || '').toLowerCase().includes(q) ||
      (r.first_ingredient || '').toLowerCase().includes(q)
    );
  }, [rows, search]);

  const chooseType = (rid, key) => {
    setSelections((prev) => {
      const next = { ...prev };
      if (!key) delete next[rid]; else next[rid] = key;
      return next;
    });
  };

  const applyBulkType = () => {
    if (!bulkType) return;
    const next = { ...selections };
    for (const r of filtered) next[r.id] = bulkType;
    setSelections(next);
    toast.success(`Sat ${filtered.length} synlige opskrifter til ${bulkType}`);
  };

  const clearAllSelections = () => setSelections({});

  const selectionCount = Object.keys(selections).length;

  const saveAll = async () => {
    if (selectionCount === 0) {
      toast.error('Vælg mindst én type før du gemmer');
      return;
    }
    setSaving(true);
    try {
      const assignments = Object.entries(selections).map(([rid, key]) => ({
        recipe_id: rid, type: key,
      }));
      const r = await axios.post(`${API}/admin/recipes/bulk-set-type`, { assignments });
      const s = r.data.summary;
      if (s.errors > 0) {
        toast.error(`${s.errors} fejlede — se resultatet nedenfor`);
      } else {
        toast.success(
          `✓ ${s.type_set} typer sat · ${s.brix_also_set} brix auto-udfyldt`
        );
      }
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kunne ikke gemme');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl" data-testid="admin-classify-recipes-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Klassificér opskrifter uden type</h1>
        <p className="text-gray-600 text-sm">
          Opskrifter importeret uden Type ender her. Vælg type per opskrift (eller sæt
          alle synlige på én gang), og klik <strong>Gem valgte</strong>. Systemet
          udfylder samtidig target_brix med typens standard hvis den var 0 eller tom.
        </p>
      </div>

      {/* Bulk toolbar */}
      <div className="flex flex-wrap items-center gap-3 mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
        <input
          type="text"
          placeholder="Søg navn, ingrediens eller beskrivelse…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm flex-1 min-w-[220px]"
          data-testid="classify-search"
        />
        <span className="text-xs text-gray-500" data-testid="classify-count">
          {filtered.length} af {rows.length}
        </span>
        <select
          value={bulkType}
          onChange={(e) => setBulkType(e.target.value)}
          className="px-3 py-1.5 border border-gray-300 rounded-md text-sm"
          data-testid="classify-bulk-type-select"
        >
          <option value="">— Bulk-vælg type —</option>
          {TYPE_OPTIONS.map((t) => (
            <option key={t.key} value={t.key}>{t.label} ({t.brix}°Bx)</option>
          ))}
        </select>
        <button
          type="button"
          onClick={applyBulkType}
          disabled={!bulkType || filtered.length === 0}
          className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs font-semibold hover:bg-purple-700 disabled:opacity-50"
          data-testid="classify-apply-bulk-btn"
        >
          Sæt {filtered.length} synlige til {bulkType || '…'}
        </button>
        <button
          type="button"
          onClick={clearAllSelections}
          className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs hover:bg-gray-50"
          data-testid="classify-clear-btn"
        >
          Ryd valg
        </button>
        <button
          type="button"
          onClick={saveAll}
          disabled={selectionCount === 0 || saving}
          className="ml-auto px-4 py-1.5 bg-emerald-600 text-white rounded-md text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50"
          data-testid="classify-save-btn"
        >
          {saving ? 'Gemmer…' : `Gem valgte (${selectionCount})`}
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">Indlæser…</div>
      ) : rows.length === 0 ? (
        <div className="p-8 text-center rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900">
          ✓ Alle opskrifter er klassificeret. Ingen udestående.
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-700 sticky top-0">
              <tr>
                <th className="px-3 py-2 font-semibold w-24">Billede</th>
                <th className="px-3 py-2 font-semibold">Opskrift</th>
                <th className="px-3 py-2 font-semibold">Hint</th>
                <th className="px-3 py-2 font-semibold text-right">Nuv. brix</th>
                <th className="px-3 py-2 font-semibold w-56">Vælg type</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.map((r) => {
                const sel = selections[r.id] || '';
                return (
                  <tr
                    key={r.id}
                    data-testid={`classify-row-${r.id}`}
                    className={sel ? 'bg-emerald-50/30' : 'hover:bg-gray-50'}
                  >
                    <td className="px-3 py-2">
                      {r.image_url && r.image_url !== '/api/images/placeholder.jpg' ? (
                        <img
                          src={r.image_url}
                          alt=""
                          className="w-16 h-12 object-cover rounded"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-16 h-12 bg-gray-100 rounded" />
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Link
                        to={`/recipes/${r.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-700 hover:underline"
                      >
                        {r.name}
                      </Link>
                      {r.description && (
                        <div className="text-xs text-gray-500 line-clamp-1 mt-0.5">
                          {r.description}
                        </div>
                      )}
                      {!r.is_published && (
                        <span className="inline-block mt-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] uppercase font-bold">
                          upubliceret
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {r.first_ingredient && (
                        <div><span className="text-gray-400">1. ingrediens:</span> {r.first_ingredient}</div>
                      )}
                      <div className="text-gray-400">{r.ingredient_count} ingredienser</div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.target_brix === 0 || r.target_brix === null || r.target_brix === undefined ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <>{Number(r.target_brix).toFixed(1)}°Bx</>
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <select
                        value={sel}
                        onChange={(e) => chooseType(r.id, e.target.value)}
                        className={`w-full px-2 py-1 text-sm border rounded ${
                          sel ? 'border-emerald-400 bg-emerald-50 font-semibold' : 'border-gray-300'
                        }`}
                        data-testid={`classify-select-${r.id}`}
                      >
                        <option value="">— Ikke valgt —</option>
                        {TYPE_OPTIONS.map((t) => (
                          <option key={t.key} value={t.key}>
                            {t.label} → {t.brix}°Bx
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-8 text-center text-gray-500">
                    Ingen rækker matcher søgningen.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-500">
        Bulk-valget skriver kun <code>type</code> — og udfylder{' '}
        <code>target_brix</code> med typens standard hvis den er 0/tom.
        Manuelt indtastede brix-værdier bevares.
      </div>
    </div>
  );
};

export default AdminClassifyRecipesPage;
